import { SadFaceIcon } from '@components/icons/FaceIcon';
import {
  type FC,
  type ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { type IconBaseProps } from 'react-icons';

interface Props {
  children?: ReactNode;
}

interface SeverityContextType {
  actionRequired: boolean;
  setActionRequired: (value: boolean) => void;
  getSeverityBorderColor: (value?: string) => string;
  getSeveritySmileyIcon: (
    value?: string,
    props?: IconBaseProps
  ) => ReactNode | null;
}

const initialValue = {
  actionRequired: false,
  setActionRequired: () => {},
  getSeverityBorderColor: () => '',
  getSeveritySmileyIcon: () => null,
};

const SeverityContext = createContext<SeverityContextType>(initialValue);

export const useSeverity = (): SeverityContextType => {
  const context = useContext(SeverityContext);
  if (!context) {
    throw new Error('useSeverity must be used within a SeverityProvider');
  }
  return context;
};

const SeverityProvider: FC<Props> = ({ children }) => {
  const [actionRequired, setActionRequired] = useState<boolean>(false);

  const getSeverityBorderColor = (severityColor?: string): string => {
    if (severityColor === 'RED') {
      setActionRequired(true);
      return 'border border-error';
    } else if (severityColor === 'GREEN') return 'border border-success';
    return 'border border-gray-10';
  };

  const getSeveritySmileyIcon = (
    severityColor?: string,
    props?: IconBaseProps
  ): ReactNode | null => {
    if (severityColor === 'RED') {
      setActionRequired(true);
      return <SadFaceIcon {...props} />;
    }
  };

  return (
    <SeverityContext.Provider
      value={{
        actionRequired,
        setActionRequired,
        getSeverityBorderColor,
        getSeveritySmileyIcon,
      }}
    >
      {children}
    </SeverityContext.Provider>
  );
};

export { SeverityContext, SeverityProvider };
